import { CustomerType } from '@superdispatch/sdk';
import { ColorDynamic, Inline, Tag } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { ReactNode, useMemo } from 'react';
import { TerminalIcon } from 'shared/icons/TerminalIcon';
import styled from 'styled-components';
import { BusinessTypeTag } from './BusinessTypeTag';

const Waypoints = styled.ul`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  margin: 0;
  padding: 0;
`;

const WaypointSeparator = styled.div`
  flex: 0;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const WaypointDot = styled.div<{
  $variant: 'origin' | 'destination' | 'terminal';
  $dense?: boolean;
  $hasRoundDots?: boolean;
  hasOpacity?: boolean;
}>`
  display: flex;
  padding: 2px;
  align-self: baseline;
  margin-top: ${({ $dense }) => ($dense ? '9px' : '7px')};

  border-radius: ${({ $variant, $hasRoundDots }) =>
    $variant === 'origin' || $hasRoundDots ? '50%' : '12.5%'};
  border: 2px solid transparent;
  opacity: ${({ hasOpacity }) => (hasOpacity ? 0.5 : 1)};

  background-color: ${({ $variant }) =>
    $variant === 'origin'
      ? ColorDynamic.Yellow300
      : $variant === 'terminal'
        ? ColorDynamic.Purple300
        : ColorDynamic.Green300};
`;

const waypointSpaces = { none: '0px', small: '4px', medium: '8px' };
const Waypoint = styled.li<{ $space?: 'none' | 'small' | 'medium' }>`
  display: flex;
  position: relative;
  list-style: none;

  &:not(:first-child) {
    margin-top: ${({ $space = 'medium' }) => waypointSpaces[$space]};
  }
`;

const WaypointConnector = styled.div`
  width: 1px;
  flex-grow: 1;
  margin-top: 4px;
  margin-bottom: -11px;

  background: repeating-linear-gradient(
    ${ColorDynamic.Silver500},
    ${ColorDynamic.Silver500} 4px,
    transparent 2px,
    transparent 6px
  );
`;

const WaypointContent = styled.div<{ hasOpacity: boolean }>`
  width: 100%;
  flex-shrink: 1;
  flex-grow: 0;
  padding-left: 11px;
  display: flex;
  align-items: center;
  opacity: ${({ hasOpacity }) => (hasOpacity ? 0.5 : 1)};

  & > :not(:first-child) {
    margin-left: 8px;
  }
`;

const WaypointContentAdornment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;

  @media (min-width: 768px) {
    & > :not(:first-child) {
      margin-left: 8px;
    }
    flex-direction: initial;
    align-items: center;
  }
`;

const BusinessHoursSlot = styled(Box)`
  &:empty {
    display: none;
  }
`;

interface WaypointsProps {
  pickupDate?: ReactNode;
  pickupAddress: ReactNode;
  pickupBusinessType?: CustomerType | null;
  pickupBusinessHours?: ReactNode;

  deliveryDate?: ReactNode;
  deliveryAddress: ReactNode;
  deliveryBusinessType?: CustomerType | null;
  deliveryBusinessHours?: ReactNode;

  terminalName?: string;
  terminalAddress?: ReactNode;
  terminalMoreContent?: ReactNode;
  terminalsCount?: number;

  currentLeg?: number | null;
  dense?: boolean;
  space?: 'none' | 'small' | 'medium';
  inline?: boolean;
  hasRoundDots?: boolean;
}

export function LoadWaypoints({
  pickupDate,
  deliveryDate,
  pickupAddress,
  deliveryAddress,
  pickupBusinessType,
  deliveryBusinessType,
  pickupBusinessHours,
  deliveryBusinessHours,
  terminalAddress,
  terminalName,
  terminalsCount,
  terminalMoreContent,
  currentLeg,
  dense,
  space = 'medium',
  inline = false,
  hasRoundDots = false,
}: WaypointsProps) {
  const pickupContent = (
    <>
      <WaypointContentAdornment>
        <div>{pickupAddress}</div>
        {!!pickupBusinessType && (
          <BusinessTypeTag businessType={pickupBusinessType} />
        )}
      </WaypointContentAdornment>
      {!!pickupBusinessHours && (
        <BusinessHoursSlot marginTop="xxsmall">
          {pickupBusinessHours}
        </BusinessHoursSlot>
      )}
      {!!pickupDate && <TextBox color="secondary">{pickupDate}</TextBox>}
    </>
  );

  const terminalContent = (
    <WaypointContentAdornment>
      <div>{terminalAddress}</div>
      {!!terminalName && (
        <Tag variant="subtle" color="dark">
          <Inline
            space="xxsmall"
            verticalAlign="center"
            horizontalAlign="center"
          >
            <Box paddingTop="xxsmall">
              <TerminalIcon />
            </Box>
            {terminalName}
          </Inline>
        </Tag>
      )}
    </WaypointContentAdornment>
  );

  const deliveryContent = (
    <>
      <WaypointContentAdornment>
        <div>{deliveryAddress}</div>
        {!!deliveryBusinessType && (
          <BusinessTypeTag businessType={deliveryBusinessType} />
        )}
      </WaypointContentAdornment>
      {!!deliveryBusinessHours && (
        <BusinessHoursSlot marginTop="xxsmall">
          {deliveryBusinessHours}
        </BusinessHoursSlot>
      )}
      {!!deliveryDate && <TextBox color="secondary">{deliveryDate}</TextBox>}
    </>
  );

  const wayPointsCount = useMemo(() => {
    let pointsCount = 0;
    if (pickupAddress) pointsCount++;
    if (deliveryAddress) pointsCount++;
    if (terminalsCount) pointsCount += terminalsCount;
    return pointsCount;
  }, [pickupAddress, deliveryAddress, terminalsCount]);

  const hasTerminal = terminalsCount && terminalsCount > 0;
  const isPickupPointNotAvailable = currentLeg && currentLeg > 1 && hasTerminal;
  const isDeliveryPointNotAvailable =
    (currentLeg && hasTerminal && currentLeg === 1 && terminalsCount === 1) ||
    (currentLeg &&
      hasTerminal &&
      currentLeg < wayPointsCount - 1 &&
      terminalsCount > 1);
  const firstTerminalPointNotAvailable =
    currentLeg && hasTerminal && currentLeg > 2 && terminalsCount > 2;
  const isNextTerminalPointNotAvailable =
    (currentLeg &&
      hasTerminal &&
      terminalsCount > 1 &&
      currentLeg !== 2 &&
      currentLeg < 3) ||
    currentLeg === 1;

  return (
    <Waypoints>
      <Waypoint>
        <WaypointSeparator>
          <WaypointDot
            $variant="origin"
            $dense={dense}
            $hasRoundDots={hasRoundDots}
            hasOpacity={!!isPickupPointNotAvailable}
          />
          <WaypointConnector />
        </WaypointSeparator>

        <WaypointContent hasOpacity={!!isPickupPointNotAvailable}>
          <TextBox variant="heading-4">
            {inline ? (
              <Inline verticalAlign="center" space="xsmall">
                {pickupContent}
              </Inline>
            ) : (
              pickupContent
            )}
          </TextBox>
        </WaypointContent>
      </Waypoint>

      {(terminalAddress || terminalName) && (
        <Waypoint $space={space}>
          <WaypointSeparator>
            <WaypointDot
              $variant="terminal"
              $dense={dense}
              $hasRoundDots={hasRoundDots}
              hasOpacity={!!firstTerminalPointNotAvailable}
            />
            <WaypointConnector />
          </WaypointSeparator>

          <WaypointContent hasOpacity={!!firstTerminalPointNotAvailable}>
            <TextBox variant="heading-4">
              {inline ? (
                <Inline verticalAlign="center" space="xsmall">
                  {terminalContent}
                </Inline>
              ) : (
                terminalContent
              )}
            </TextBox>
          </WaypointContent>
        </Waypoint>
      )}

      {terminalMoreContent && (
        <Waypoint $space={space}>
          <WaypointSeparator>
            <WaypointDot
              $variant="terminal"
              $dense={dense}
              $hasRoundDots={hasRoundDots}
              hasOpacity={!!isNextTerminalPointNotAvailable}
            />
            <WaypointConnector />
          </WaypointSeparator>

          <WaypointContent hasOpacity={!!isNextTerminalPointNotAvailable}>
            {terminalMoreContent}
          </WaypointContent>
        </Waypoint>
      )}

      <Waypoint $space={space}>
        <WaypointSeparator>
          <WaypointDot
            $variant="destination"
            $dense={dense}
            $hasRoundDots={hasRoundDots}
            hasOpacity={!!isDeliveryPointNotAvailable}
          />
        </WaypointSeparator>

        <WaypointContent hasOpacity={!!isDeliveryPointNotAvailable}>
          <TextBox variant="heading-4">
            {inline ? (
              <Inline verticalAlign="center" space="xsmall">
                {deliveryContent}
              </Inline>
            ) : (
              deliveryContent
            )}
          </TextBox>
        </WaypointContent>
      </Waypoint>
    </Waypoints>
  );
}
